import DashboardLayout from "@/components/layouts/DashboardLayout";
import { useUserDetails } from "@/hooks/api/useUserDetails";
import { useContacts, useContactLimits, useCreateContact } from "@/hooks/api/useContacts";
import { useState } from "react";
import { User, Tag } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import AddMemoryModal from "@/components/memories/AddMemoryModal";
import PersonalizeContactModal from "@/components/contacts/PersonalizeContactModal";
import { GoogleImportFlow } from "@/components/dashboard/GoogleImportFlow";
import type { Contact, ContactCreateUpdateRequest } from "@/types/api";
import { ContactPreviewCard } from "@/components/contacts/ContactPreviewCard";
import { AddContactSection } from "@/components/dashboard/AddContactSection";
import { AddMemorySection } from "@/components/dashboard/AddMemorySection";
import MemoryCard from "@/components/memories/MemoryCard";
import { QuickActions } from "@/components/dashboard/QuickActions";
import { useAnalytics } from "@/hooks/useAnalytics";

const People = () => {
  const { data: userDetails } = useUserDetails();
  const { data: contactsData, refetch: refetchContacts } = useContacts();
  const { data: contactLimits } = useContactLimits();
  const { toast } = useToast();
  const createContact = useCreateContact();
  const { trackButtonClick, trackUserInteraction } = useAnalytics();

  const [showManualContactSection, setShowManualContactSection] = useState(false);
  const [showGoogleImportFlow, setShowGoogleImportFlow] = useState(false);
  const [showAddMemoryModal, setShowAddMemoryModal] = useState(false);
  const [showAddMemorySection, setShowAddMemorySection] = useState(false);
  const [showPersonalizeModal, setShowPersonalizeModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [selectedContactIndex, setSelectedContactIndex] = useState(0);
  const [newContact, setNewContact] = useState<Contact | null>(null);
  const [previewFormState, setPreviewFormState] = useState({
    contactName: '',
    phone: '',
    relationshipType: '',
    birthday: undefined as Date | undefined,
    relationshipData: {} as Record<string, any>,
    selectedAvatar: '',
  });
  const [memoryPreviewState, setMemoryPreviewState] = useState({
    contactId: undefined as number | undefined,
    contactName: '',
    contactAvatar: '',
    memories: [] as any[],
  });

  const firstName = userDetails?.first_name || "there";
  const userContacts = contactsData?.results || [];

  const handleCreateContact = () => {
    trackButtonClick('Add Contact', 'people');
    if (contactLimits && !contactLimits.can_create) {
      toast({
        title: "Contact limit reached",
        description: contactLimits.message || "You have reached your contact limit.",
        variant: "destructive",
      });
      return;
    }
    setShowManualContactSection(true);
  };

  const handleImportFromGoogle = () => {
    trackButtonClick('Import from Google', 'people');
    if (contactLimits && !contactLimits.can_create) {
      toast({
        title: "Contact limit reached",
        description: contactLimits.message || "You have reached your contact limit.",
        variant: "destructive",
      });
      return;
    }
    setShowManualContactSection(false);
    setShowGoogleImportFlow(true);
  };

  const handleManualSave = async (contactData: ContactCreateUpdateRequest) => {
    try {
      const createdContact = await createContact.mutateAsync(contactData);
      setNewContact(createdContact);
      setShowManualContactSection(false);
      setShowPersonalizeModal(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create contact",
        variant: "destructive",
      });
    }
  };

  const handleCloseManualSection = () => {
    setShowManualContactSection(false);
    setShowGoogleImportFlow(false);
  };

  const handleAddMemory = () => {
    setShowManualContactSection(false);
    setShowGoogleImportFlow(false);
    setShowAddMemorySection(true);
  };

  const handleContactClick = (contact: Contact, index: number) => {
    trackUserInteraction('View Contact', contact.name);
    // Navigate to contact detail page
    window.location.href = `/contacts/${contact.id}`;
  };

  const handlePersonalizeSuccess = () => {
    refetchContacts();
    setNewContact(null);
  };

  const handlePersonalizeComplete = () => {
    setShowPersonalizeModal(false);
    setNewContact(null);
    toast({
      title: "Success",
      description: "Contact added successfully!",
    });
    refetchContacts();
  };

  const handleGoogleImportComplete = () => {
    setShowGoogleImportFlow(false);
    refetchContacts();
  };

  return (
    <DashboardLayout>
      <div className="container mx-auto px-4 md:px-8">
        <div className="py-6 md:py-8">
          {/* Header Section */}
          <div className="mb-8">
            <h2 className="text-2xl font-medium font-euclid text-wabisabi-plum mb-2">
              Hi {firstName}, your people, your circle.
            </h2>
            <p className="text-gray-600">Here's everyone you care about, in one thoughtful place.</p>
          </div>

          {/* Counts Section */}
          <div className="grid grid-cols-3 gap-3 md:gap-6 mb-8">
            {/* Upcoming Card */}
            <div className="bg-white rounded-lg p-3 md:p-6 text-center">
              <h3 className="text-[12px] md:text-[14px] font-medium text-black mb-4 md:mb-[35px]">Upcoming</h3>
              <p className="text-[24px] md:text-[32px] font-bold text-black">0</p>
            </div>

            {/* Past Due Card */}
            <div className="bg-white rounded-lg p-3 md:p-6 text-center">
              <h3 className="text-[12px] md:text-[14px] font-medium text-black mb-4 md:mb-[35px]">Past Due</h3>
              <p className="text-[24px] md:text-[32px] font-bold text-black">0</p>
            </div>

            {/* Recurring Card */}
            <div className="bg-white rounded-lg p-3 md:p-6 text-center">
              <h3 className="text-[12px] md:text-[14px] font-medium text-black mb-4 md:mb-[35px]">Recurring</h3>
              <p className="text-[24px] md:text-[32px] font-bold text-black">0</p>
            </div>
          </div>

          {/* Quick Actions Section */}
          <QuickActions
            onAddContact={handleCreateContact}
            onImportGoogle={handleImportFromGoogle}
            onAddMemory={handleAddMemory}
            showImportGoogle={true}
            showSetReminder={false}
          />

          {/* Everyone in your orbit Section or Manual Contact Form or Add Memory Section */}
          <div className="mb-8">
            {showManualContactSection ? (
              <div className="grid grid-cols-1 lg:grid-cols-[1fr,auto] gap-6">
                <AddContactSection
                  onClose={handleCloseManualSection}
                  onGoogleImport={handleImportFromGoogle}
                  onManualSave={handleManualSave}
                  onFormStateChange={setPreviewFormState}
                  skipSelection={true}
                />
                {previewFormState.contactName && (
                  <ContactPreviewCard
                    contactName={previewFormState.contactName}
                    phone={previewFormState.phone}
                    relationshipType={previewFormState.relationshipType}
                    birthday={previewFormState.birthday}
                    relationshipData={previewFormState.relationshipData}
                    avatar={previewFormState.selectedAvatar}
                  />
                )}
              </div>
            ) : showAddMemorySection ? (
              <div className="grid grid-cols-1 lg:grid-cols-[1fr,auto] gap-6">
                <AddMemorySection
                  onClose={() => setShowAddMemorySection(false)}
                  onSuccess={() => setShowAddMemorySection(false)}
                  onFormStateChange={setMemoryPreviewState}
                />
                {memoryPreviewState.contactName && (
                  <div className="bg-white rounded-lg p-6 w-[380px]">
                    <h4 className="text-[20px] font-bold text-black mb-6">
                      Recent memories of {memoryPreviewState.contactName}
                    </h4>
                    {memoryPreviewState.memories.length > 0 ? (
                      <div className="space-y-3">
                        {memoryPreviewState.memories.map((memory) => (
                          <MemoryCard
                            key={memory.id}
                            memory={{
                              ...memory,
                              contact_avatar: memoryPreviewState.contactAvatar,
                              contact_name: memoryPreviewState.contactName,
                            }}
                            variant="compact"
                            showAvatar={true}
                            showSuggestionCount={false}
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-gray-500 py-4">No memories yet for this contact</p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                <h3 className="text-[20px] font-bold font-euclid text-wabisabi-plum mb-4">Everyone in your orbit</h3>
                {userContacts.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {userContacts.slice(0, 6).map((contact, index) => (
                      <div
                        key={contact.id}
                        onClick={() => handleContactClick(contact, index)}
                        className={`rounded-lg h-[214px] cursor-pointer transition-colors p-6 flex flex-col items-center justify-center ${
                          index === selectedContactIndex
                            ? "bg-[#F4978E]"
                            : "bg-white hover:bg-[#F4978E] group"
                        }`}
                      >
                        {/* Contact Avatar */}
                        <Avatar className="w-20 h-20 mb-4">
                          {contact.avatar ? (
                            <AvatarImage src={`/images/avatars/${contact.avatar}`} alt={contact.name} />
                          ) : null}
                          <AvatarFallback className="bg-gray-200">
                            <User className="w-10 h-10 text-gray-500" />
                          </AvatarFallback>
                        </Avatar>

                        {/* Contact Name */}
                        <h4 className={`text-[18px] font-bold text-center mb-2 ${
                          index === selectedContactIndex
                            ? "text-white"
                            : "text-black group-hover:text-white"
                        }`}>
                          {contact.name}
                        </h4>

                        {/* Contact Relationship */}
                        {contact.relationship_type && (
                          <div className="flex items-center gap-1">
                            <Tag className={`w-4 h-4 ${
                              index === selectedContactIndex
                                ? "text-white"
                                : "text-gray-600 group-hover:text-white"
                            }`} />
                            <p className={`text-[14px] ${
                              index === selectedContactIndex
                                ? "text-white"
                                : "text-gray-600 group-hover:text-white"
                            }`}>
                              {contact.relationship_type}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg p-12 text-center">
                    <p className="text-gray-500">No contacts yet. Add your first contact to get started!</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Google Import Flow */}
      {showGoogleImportFlow && (
        <GoogleImportFlow
          onComplete={handleGoogleImportComplete}
          onCancel={() => setShowGoogleImportFlow(false)}
        />
      )}

      {/* Add Memory Modal */}
      <AddMemoryModal
        isOpen={showAddMemoryModal}
        onClose={() => setShowAddMemoryModal(false)}
      />

      {/* Personalize Contact Modal for existing contacts */}
      {selectedContact && !newContact && (
        <PersonalizeContactModal
          isOpen={showPersonalizeModal}
          onClose={() => setShowPersonalizeModal(false)}
          contact={selectedContact}
          onSuccess={handlePersonalizeSuccess}
        />
      )}

      {/* Personalize Modal for newly created contact */}
      {newContact && (
        <PersonalizeContactModal
          isOpen={showPersonalizeModal}
          onClose={() => setShowPersonalizeModal(false)}
          contact={newContact}
          onSuccess={handlePersonalizeComplete}
        />
      )}
    </DashboardLayout>
  );
};

export default People;
